/**
 * Horizontal git-graph-style timeline of the snapshot chain. Replaces
 * the older vertical SnapshotChainView for the DraftSheet's Lineage
 * tab — denser, more legible, reads like git's commit graph.
 *
 * Visual:
 *
 *      ●───●───○───○───●───○───◎          (current)
 *
 *   • Filled dot   = committed snapshot's source (creator content_hash present)
 *   • Empty dot    = working-buffer snapshot
 *   • Active outer = the snapshot the user is currently inspecting
 *   • Cyan trace   = phosphor connector between dots
 *   • Hover        = tooltip with hash + relative time
 *
 * Each dot is a click target that activates that snapshot. The track
 * scrolls horizontally if the chain exceeds the available width.
 */
import { useSnapshotLineage } from "@features/circuits/hooks/useCircuitIr";
import {
  Box,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

interface OpTimelineStripProps {
  snapshotId: string | null;
  onSelect?: (snapshotId: string) => void;
}

export function OpTimelineStrip({
  snapshotId,
  onSelect,
}: OpTimelineStripProps) {
  const { data, isLoading } = useSnapshotLineage(snapshotId);

  if (!snapshotId) return null;
  if (isLoading) {
    return (
      <Stack sx={{ p: 2, alignItems: "center" }}>
        <CircularProgress size={16} />
      </Stack>
    );
  }
  if (!data || data.chain.length === 0) return null;

  // Reverse the chain so the OLDEST snapshot is on the left and the
  // newest on the right — matches git log's left-to-right convention
  // when read top-to-bottom (oldest first).
  const chain = [...data.chain].reverse();

  return (
    <Box sx={{ p: 1.5 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0}
        sx={{
          overflowX: "auto",
          py: 1.5,
          // Cleaner scrollbar — this strip is a focal point.
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": { height: 4 },
        }}
      >
        {chain.map((entry, i) => {
          const isActive = entry.snapshot_id === snapshotId;
          const isLast = i === chain.length - 1;
          return (
            <Stack
              key={entry.snapshot_id}
              direction="row"
              alignItems="center"
              sx={{ flexShrink: 0 }}
            >
              <Tooltip
                title={
                  <Box>
                    <Box sx={{ fontFamily: "monospace" }}>
                      {entry.content_hash.slice(0, 16)}…
                    </Box>
                    <Box>{formatRelative(entry.created_at)}</Box>
                  </Box>
                }
              >
                <Box
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelect?.(entry.snapshot_id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onSelect?.(entry.snapshot_id);
                    }
                  }}
                  sx={(t) => ({
                    width: isActive ? 14 : 10,
                    height: isActive ? 14 : 10,
                    borderRadius: "50%",
                    cursor: "pointer",
                    background: isActive
                      ? t.palette.accent.main
                      : t.palette.surface.default,
                    border: `1.5px solid ${
                      isActive ? t.palette.accent.main : t.palette.border.strong
                    }`,
                    boxShadow: isActive ? t.palette.accent.glow : "none",
                    transition: `transform ${t.motion.fast}, box-shadow ${t.motion.fast}`,
                    "&:hover": {
                      transform: "scale(1.2)",
                      borderColor: t.palette.accent.main,
                    },
                    flexShrink: 0,
                  })}
                />
              </Tooltip>
              {!isLast && (
                <Box
                  sx={(t) => ({
                    width: 28,
                    height: 1.5,
                    background: t.palette.accent.softer,
                    flexShrink: 0,
                  })}
                />
              )}
            </Stack>
          );
        })}
      </Stack>
      <Typography
        variant="caption"
        sx={{ color: "text.disabled", display: "block", mt: 0.5 }}
      >
        {chain.length} snapshot{chain.length === 1 ? "" : "s"} ·{" "}
        {formatRelative(chain[chain.length - 1]?.created_at)}
      </Typography>
    </Box>
  );
}

function formatRelative(iso: string | undefined): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const seconds = Math.floor((Date.now() - t) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
